import React, { useMemo } from "react";

import { faCircleNotch, faCheck } from "@fortawesome/pro-light-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import type { Theme } from "@material-ui/core";
import { makeStyles, createStyles, Button } from "@material-ui/core";

import type { FormikHelpers, FormikProps } from "formik";
import { Formik, Form as FormikForm } from "formik";
import { useObservableState, useObservable } from "observable-hooks";
import { useTranslation } from "react-i18next";
import type { Observable } from "rxjs";
import { Subject } from "rxjs";
import { switchMap, withLatestFrom, map } from "rxjs/operators";
import type * as Yup from "yup";

import type { IResponse, IResponseWithLoading } from "App/Modules/Models";
import { loadingCompletedResponse } from "../../Utils";

import type { Response } from "./TransformResponse";
import { transformResponse } from "./TransformResponse";
import type { ValidationResponseErrors } from "./ValidationError";
import { ValidationError } from "./ValidationError";

export type FormProps<TFormData extends Record<string, any>, TSubmitResponse> = {
    buttonContainer?: React.ComponentType<any>;
    children: (props: FormikProps<TFormData>) => React.ReactNode;
    extraButtons?: (props: FormikProps<TFormData>) => React.ReactNode;
    initialValues: TFormData;
    onSubmitSuccessful: (data: IResponse<TSubmitResponse>) => void;
    /** Return true to block error from showing */
    onSubmitFailed?: (data: IResponse<TSubmitResponse>, validationErrors: ValidationResponseErrors) => boolean;
    onSubmitting: (data: TFormData) => Response<TSubmitResponse>;
    validationSchema: Yup.ObjectSchema<TFormData>;
    classes?: { validationErrorAlert?: string };
};

const useStyles = makeStyles((theme: Theme) =>
    createStyles({
        alert: {
            marginBottom: theme.spacing(2),
            marginTop: theme.spacing(2),
        },
    }),
);

export const Form = <TFormData extends Record<string, any>, TSubmitResponse = TFormData>(
    props: FormProps<TFormData, TSubmitResponse>,
) => {
    const { t } = useTranslation();
    const { buttonContainer, extraButtons, initialValues, onSubmitFailed, onSubmitSuccessful, onSubmitting, children } =
        props;

    const CustomButtonBar = buttonContainer ?? (() => <div />);

    const classes = useStyles();

    const submitSubject = useMemo(
        () =>
            new Subject<{
                data: TFormData;
                formikHelpers: FormikHelpers<TFormData>;
            }>(),
        [],
    );

    const submitResponse$: Observable<IResponseWithLoading<TSubmitResponse>> = useObservable(
        (input$: Observable<[typeof onSubmitting, typeof onSubmitSuccessful, typeof onSubmitFailed]>) => {
            return submitSubject.pipe(withLatestFrom(input$)).pipe(
                switchMap(([{ data, formikHelpers }, [_onSubmitting, _onSubmitSuccessful, _onSubmitFailed]]) =>
                    transformResponse(_onSubmitting(data)).pipe(
                        map((response) => {
                            formikHelpers.setSubmitting(response.isLoading === true);
                            if (!response.hasError && response.isSuccessful) {
                                _onSubmitSuccessful(response);
                            } else if (response.hasError) {
                                if (_onSubmitFailed) {
                                    const stopError = _onSubmitFailed(response, response.error?.errors);
                                    if (stopError) {
                                        return loadingCompletedResponse<TSubmitResponse>();
                                    }
                                }
                            }
                            return response;
                        }),
                    ),
                ),
            );
        },
        [onSubmitting, onSubmitSuccessful, onSubmitFailed],
    );

    const submitResult = useObservableState(submitResponse$, {
        isLoading: false,
    });

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={props.validationSchema}
            onSubmit={(data, formikHelpers) => {
                submitSubject.next({ data, formikHelpers });
            }}
        >
            {(formikProps) => (
                <FormikForm>
                    {children(formikProps)}
                    <ValidationError
                        response={submitResult}
                        className={props?.classes?.validationErrorAlert ?? classes.alert}
                    />
                    <CustomButtonBar>
                        {extraButtons(formikProps)}
                        <Button
                            type="submit"
                            variant="contained"
                            color="primary"
                            size="large"
                            disabled={formikProps.isSubmitting}
                            startIcon={
                                formikProps.isSubmitting ? (
                                    <FontAwesomeIcon icon={faCircleNotch} spin fixedWidth />
                                ) : (
                                    <FontAwesomeIcon fixedWidth icon={faCheck} />
                                )
                            }
                        >
                            {t("Gui.Buttons.Apply")}
                        </Button>
                    </CustomButtonBar>
                </FormikForm>
            )}
        </Formik>
    );
};
